// The REDLiner shell↔SDK wire contract. BINDING RULE (Phase-1a spike, 2026-07-19): the two sides
// communicate by postMessage ONLY — never via window handles. Entra's flighted COOP header severs
// handles mid-flow (see the "Vendor Bug Reports & Incidents" board, MSAL COOP item); messages are
// immune. Every message carries the protocol version; receivers must ignore unknown versions and
// unknown types rather than throw — the shell and a target's embedded SDK upgrade independently.

export const PROTOCOL_VERSION = 1;

/** RED = Requirements · Enhancements · Defects. */
export type RedType = "requirement" | "enhancement" | "defect";
export const RED_TYPES: readonly RedType[] = ["requirement", "enhancement", "defect"];

/** Server-enforced annotation cap per work-order batch; mirrored client-side so trays never lie. */
export const BATCH_CAP = 20;

/** Structured capture of one picked element, as recorded by red-sdk inside the target app. */
export interface ElementContext {
  route: string;
  component: string | null;
  selector: string;
  tag: string;
  text: string | null;
  rect: { x: number; y: number; w: number; h: number };
  viewport: { w: number; h: number };
  dpr: number;
  theme: string | null;
  ua: string;
  /** Target app's own version string, supplied via SDK config. */
  version: string;
  at: string;
}

export interface Annotation {
  id: string;
  type: RedType;
  msg: string;
  context: ElementContext;
}

interface Envelope {
  v: typeof PROTOCOL_VERSION;
  type: `redliner:${"shell" | "sdk"}:${string}`;
}

export type ShellToSdkMessage =
  | (Envelope & { type: "redliner:shell:ping" })
  | (Envelope & { type: "redliner:shell:pick-start"; requestId: string })
  | (Envelope & { type: "redliner:shell:pick-cancel"; requestId: string });

export type SdkToShellMessage =
  | (Envelope & {
      type: "redliner:sdk:hello";
      sdkVersion: string;
      app: { name: string; version: string };
      framed: boolean;
    })
  | (Envelope & { type: "redliner:sdk:pong" })
  | (Envelope & { type: "redliner:sdk:pick-result"; requestId: string; context: ElementContext })
  | (Envelope & { type: "redliner:sdk:pick-cancelled"; requestId: string });

export type RedlinerMessage = ShellToSdkMessage | SdkToShellMessage;

function hasEnvelope(data: unknown): data is Envelope {
  if (typeof data !== "object" || data === null) return false;
  const d = data as Record<string, unknown>;
  return d.v === PROTOCOL_VERSION && typeof d.type === "string" && d.type.startsWith("redliner:");
}

export function isShellMessage(data: unknown): data is ShellToSdkMessage {
  return hasEnvelope(data) && data.type.startsWith("redliner:shell:");
}

export function isSdkMessage(data: unknown): data is SdkToShellMessage {
  return hasEnvelope(data) && data.type.startsWith("redliner:sdk:");
}

export function isRedlinerMessage(data: unknown): data is RedlinerMessage {
  return isShellMessage(data) || isSdkMessage(data);
}
